// src/pages/AddListeningQuestionsToSTEP.jsx
import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowRight,
  Plus,
  Trash2,
  Loader2,
  AlertCircle,
  CheckCircle,
  Headphones,
  Image as ImageIcon,
  X,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Highlighter,
} from "lucide-react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import { stepQuestionsAPI } from "../../services/stepService";
import TranslationField from "../../components/TranslationField";

// ============================================
// Rich Text (question_text) — shared editor
// Stores question_text as HTML (<strong>/<em>/<u>/<mark>)
// ============================================
function ToolbarBtn({ active, onClick, children, title }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`p-1.5 rounded-md transition-colors ${
        active
          ? "bg-cyan-100 text-cyan-700"
          : "text-gray-500 hover:bg-gray-100"
      }`}
    >
      {children}
    </button>
  );
}

function QuestionTextEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Highlight.configure({ multicolor: false }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none min-h-[70px] px-3 py-2 focus:outline-none",
      },
    },
  });

  if (!editor) return null;

  return (
    <div className="border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-cyan-500 bg-white">
      <div className="flex gap-1 border-b border-gray-200 p-1 bg-gray-50 rounded-t-lg">
        <ToolbarBtn
          title="Bold"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Italic"
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Underline"
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <UnderlineIcon className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Highlight"
          active={editor.isActive("highlight")}
          onClick={() => editor.chain().focus().toggleHighlight().run()}
        >
          <Highlighter className="w-4 h-4" />
        </ToolbarBtn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

function isRichTextEmpty(html) {
  return !html || html.replace(/<[^>]*>/g, "").trim() === "";
}

const EMPTY_QUESTION = () => ({
  question_text: "",
  options: ["", "", "", ""],
  correct_answer: "",
  explanation: "",
  english_explanation: "",
  translation: "",
  points: 1,
  imageFile: null,
  imagePreview: null,
});

export default function AddListeningQuestionsToSTEP() {
  const { skillId, audioId } = useParams();
  const navigate = useNavigate();

  const [questions, setQuestions] = useState([EMPTY_QUESTION()]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [successCount, setSuccessCount] = useState(0);

  const updateQuestion = (idx, field, value) => {
    setQuestions((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const updateOption = (qIdx, optIdx, value) => {
    setQuestions((prev) => {
      const updated = [...prev];
      const options = [...updated[qIdx].options];
      options[optIdx] = value;
      updated[qIdx] = { ...updated[qIdx], options };
      return updated;
    });
  };

  const handleImageChange = (idx, e) => {
    const file = e.target.files[0];
    if (file) {
      updateQuestion(idx, "imageFile", file);
      updateQuestion(idx, "imagePreview", URL.createObjectURL(file));
    }
  };

  const removeImage = (idx) => {
    updateQuestion(idx, "imageFile", null);
    updateQuestion(idx, "imagePreview", null);
  };

  const addQuestion = () => setQuestions((prev) => [...prev, EMPTY_QUESTION()]);

  const removeQuestion = (idx) =>
    setQuestions((prev) => prev.filter((_, i) => i !== idx));

  const validate = () => {
    const newErrors = [];
    questions.forEach((q, i) => {
      const qErrors = {};
      if (isRichTextEmpty(q.question_text))
        qErrors.question_text = "Question is required";
      if (q.options.filter((o) => o.trim()).length < 2)
        qErrors.options = "You must add at least two options";
      if (!q.correct_answer.trim())
        qErrors.correct_answer = "Correct answer is required";
      else if (!q.options.includes(q.correct_answer))
        qErrors.correct_answer = "Correct answer must match one of the options";
      newErrors[i] = qErrors;
    });
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (validationErrors.some((e) => Object.keys(e).length > 0)) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    let count = 0;
    const newErrors = Array(questions.length).fill({});

    for (let i = 0; i < questions.length; i++) {
      try {
        const q = questions[i];
        const formData = new FormData();
        formData.append("question_text", q.question_text);
        formData.append("explanation", q.explanation);
        formData.append("english_explanation", q.english_explanation);
        formData.append("translation", q.translation || "");
        formData.append("points", q.points);
        formData.append("correct_answer", q.correct_answer);
        formData.append("options", JSON.stringify(q.options.filter((o) => o.trim())));
        if (q.imageFile) {
          formData.append("question_image", q.imageFile);
        }

        await stepQuestionsAPI.createListeningQuestion(audioId, formData);
        count++;
      } catch (err) {
        newErrors[i] = { general: "Failed to submit this question" };
      }
    }

    setSuccessCount(count);
    setErrors(newErrors);
    setLoading(false);

    if (count === questions.length) {
      setTimeout(() => navigate(`/dashboard/step/skills/${skillId}`), 1500);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link
          to={`/dashboard/step/skills/${skillId}`}
          className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
        >
          <ArrowRight className="w-5 h-5" />
        </Link>
        <div className="bg-cyan-50 p-3 rounded-xl">
          <Headphones className="w-6 h-6 text-cyan-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Add Questions to Recording
          </h1>
          <p className="text-sm text-cyan-600 font-medium">STEP — Listening</p>
        </div>
      </div>

      {/* Success Banner */}
      {successCount > 0 && (
        <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          {successCount} question(s) added successfully! Redirecting...
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {questions.map((q, idx) => (
          <div key={idx} className="card border border-cyan-100 space-y-4">
            {/* Question Header */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-cyan-600 bg-cyan-50 px-3 py-1 rounded-full">
                Question {idx + 1}
              </span>
              {questions.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeQuestion(idx)}
                  className="text-red-400 hover:text-red-600 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>

            {errors[idx]?.general && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                {errors[idx].general}
              </div>
            )}

            {/* Question Text */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Question Text <span className="text-red-500">*</span>
              </label>
              <QuestionTextEditor
                value={q.question_text}
                onChange={(html) => updateQuestion(idx, "question_text", html)}
              />
              {errors[idx]?.question_text && (
                <p className="text-xs text-red-500 mt-1">
                  {errors[idx].question_text}
                </p>
              )}
              <TranslationField
                label="ترجمة نص السؤال"
                value={q.translation}
                onChange={(v) => ((html) => updateQuestion(idx, "translation", html))(v)}
              />
            </div>

            {/* Question Image */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Question Image (Optional)
              </label>
              {q.imagePreview ? (
                <div className="relative inline-block">
                  <img
                    src={q.imagePreview}
                    alt="Preview"
                    className="w-full max-h-40 object-contain rounded-lg border border-gray-300"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(idx)}
                    className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg py-5 cursor-pointer hover:border-cyan-400 transition-colors">
                  <ImageIcon className="w-6 h-6 text-gray-400 mb-1" />
                  <span className="text-xs text-gray-500">Click to upload an image</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageChange(idx, e)}
                    className="hidden"
                  />
                </label>
              )}
            </div>

            {/* Options */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Options <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                {q.options.map((opt, oi) => (
                  <div key={oi} className="flex items-center gap-2">
                    <span className="text-xs font-bold text-gray-500 w-5">
                      {String.fromCharCode(65 + oi)}.
                    </span>
                    <input
                      type="text"
                      value={opt}
                      onChange={(e) => updateOption(idx, oi, e.target.value)}
                      placeholder={`Option ${String.fromCharCode(65 + oi)}`}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                ))}
              </div>
              {errors[idx]?.options && (
                <p className="text-xs text-red-500 mt-1">
                  {errors[idx].options}
                </p>
              )}
            </div>

            {/* Correct Answer */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Correct Answer <span className="text-red-500">*</span>
              </label>
              <select
                value={q.correct_answer}
                onChange={(e) =>
                  updateQuestion(idx, "correct_answer", e.target.value)
                }
                className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white ${
                  errors[idx]?.correct_answer
                    ? "border-red-400"
                    : "border-gray-300"
                }`}
              >
                <option value="">Select the correct answer</option>
                {q.options
                  .filter((o) => o.trim())
                  .map((opt, oi) => (
                    <option key={oi} value={opt}>
                      {String.fromCharCode(65 + q.options.indexOf(opt))}. {opt}
                    </option>
                  ))}
              </select>
              {errors[idx]?.correct_answer && (
                <p className="text-xs text-red-500 mt-1">
                  {errors[idx].correct_answer}
                </p>
              )}
            </div>

            {/* Explanation + English Explanation + Points */}
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2 space-y-3">
                {" "}
                {/* ✅ غيّر col-span-2 عشان يضم الاتنين */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Explanation (Arabic - Optional)
                  </label>
                  <input
                    type="text"
                    value={q.explanation}
                    onChange={(e) =>
                      updateQuestion(idx, "explanation", e.target.value)
                    }
                    placeholder="Explain the answer in Arabic..."
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    English Explanation (Optional)
                  </label>
                  <input
                    type="text"
                    value={q.english_explanation}
                    onChange={(e) =>
                      updateQuestion(idx, "english_explanation", e.target.value)
                    }
                    placeholder="Explain the answer in English..."
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Points
                </label>
                <input
                  type="number"
                  value={q.points}
                  onChange={(e) =>
                    updateQuestion(idx, "points", parseInt(e.target.value))
                  }
                  min="1"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>
            </div>
          </div>
        ))}

        {/* Add Question */}
        <button
          type="button"
          onClick={addQuestion}
          className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-cyan-300 text-cyan-600 rounded-xl hover:border-cyan-400 hover:bg-cyan-50 transition-colors text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          Add Another Question
        </button>

        {/* Submit */}
        <div className="flex gap-3">
          <button
            type="submit"
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-60 text-white py-3 rounded-xl font-medium transition-colors"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Save {questions.length} Question{questions.length !== 1 ? "s" : ""}
          </button>
          <Link
            to={`/dashboard/step/skills/${skillId}`}
            className="flex-1 flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-xl font-medium transition-colors"
          >
            Skip
          </Link>
        </div>
      </form>
    </div>
  );
}